import { useEffect, type RefObject } from 'react';

/**
 * Cmd/Ctrl+K focuses the chat list's filter box — the quick-switcher
 * convention Slack and Discord users expect. Suppressed while a dialog is
 * open (a Radix dialog traps focus; stealing it would fight the trap) and
 * while the chat page is unmounted, since the listener is registered only
 * for the page's lifetime. Follows the calendar arche's shortcut hook; there
 * is no app-wide shortcut registry.
 */
export function useChatSidebarShortcut(inputRef: RefObject<HTMLInputElement | null>): void {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Plain Cmd/Ctrl+K only: Ctrl+Shift+K is Firefox's console, Alt combos
      // are the browser's or the OS's.
      if (!(event.metaKey || event.ctrlKey) || event.shiftKey || event.altKey) return;
      if (event.key.toLowerCase() !== 'k') return;
      if (document.querySelector('[role="dialog"]')) return;
      const input = inputRef.current;
      if (!input) return;
      event.preventDefault();
      input.focus();
      input.select();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [inputRef]);
}
